import React from "react";
import Layout from "@/components/utu/Layout";
import { GlassCard, GoldPillButton } from "@/components/utu/atoms";
import { SEO } from "@/components/SEO";
import { Check, ArrowRight } from "lucide-react";
import { ENGAGEMENT_TIERS, BOOKING_URL } from "@/mock";

export default function EngageTiers() {
  return (
    <Layout>
      <SEO
        title="Engagement Tiers & Models"
        description="Choose how to partner with UTU Africa: 2-week Architecture Audits, 4-8 week Sprint Builds, or ongoing Fractional Lab Retainers. Structured deliverables and fixed scopes."
        url="/engage/tiers"
      />
      <section className="mx-auto max-w-6xl px-5 py-12 md:px-8">
        {/* Header Hero */}
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/5 px-3 py-1 text-xs text-[hsl(var(--utu-gold))] backdrop-blur-xl mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--utu-gold))]" />
            Engagement Models
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl mb-6">
            How We Work
          </h1>
          <p className="text-lg text-white/70 leading-relaxed">
            From short advisory audits to full-scale infrastructure builds, we partner with enterprises under clear, milestone-driven structures. No ambiguous agency hours—only concrete deliverables.
          </p>
        </div>

        {/* Pricing/Engagement Cards */}
        <div className="grid gap-8 lg:grid-cols-3 mb-16">
          {ENGAGEMENT_TIERS.map((tier) => (
            <GlassCard key={tier.id} className="p-8 flex flex-col justify-between h-full border border-white/10 hover:border-amber-500/30 transition-all duration-300">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold text-[hsl(var(--utu-gold))] uppercase tracking-wider">{tier.duration}</span>
                  <span className="text-xs text-white/50 bg-white/5 px-2.5 py-1 rounded-full border border-white/10">{tier.scope}</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{tier.name}</h3>
                <div className="text-sm font-semibold text-white/80 mb-6">{tier.priceText}</div>
                <p className="text-sm text-white/65 leading-relaxed mb-8">{tier.description}</p>
                
                <div className="h-px bg-white/10 mb-8" />
                
                <h4 className="text-xs font-bold text-white/80 uppercase tracking-wider mb-4">Deliverables Include:</h4>
                <ul className="space-y-3 mb-8">
                  {tier.deliverables.map((del, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-xs text-white/70">
                      <Check className="h-4 w-4 text-[hsl(var(--utu-gold))] shrink-0 mt-0.5" />
                      <span>{del}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <GoldPillButton className="w-full h-11 justify-center">
                    {tier.ctaText}
                    <ArrowRight className="h-4 w-4" />
                  </GoldPillButton>
                </a>
              </div>
            </GlassCard>
          ))}
        </div>

        {/* R&D Retainer details */}
        <GlassCard className="p-8 md:p-12 mb-16">
          <div className="grid gap-8 md:grid-cols-2">
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">Engagement Integrity</h2>
              <p className="text-sm text-white/65 leading-relaxed mb-4">
                We believe in high-integrity engineering. We do not extend engagements unnecessarily, nor do we suggest technologies you do not need. Every sprint is fully documented, run on private client GitHub branches, and handed over completely to your internal team at completion.
              </p>
              <p className="text-sm text-white/65 leading-relaxed">
                All IP created during the Sprint Builds and Retainers belongs 100% to the client. Our goal is to set up your team for sovereign, self-hosted independence.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white mb-4">The Engagement Lifecycle</h3>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/20 text-xs font-bold text-[hsl(var(--utu-gold))]">1</div>
                  <div>
                    <h5 className="font-semibold text-white text-sm">Architecture Audit</h5>
                    <p className="text-xs text-white/60">We evaluate your current data pipelines, database setups, and API capabilities.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/20 text-xs font-bold text-[hsl(var(--utu-gold))]">2</div>
                  <div>
                    <h5 className="font-semibold text-white text-sm">Milestone Sprint Build</h5>
                    <p className="text-xs text-white/60">Incremental coding sprints on secure environments with continuous testing and review.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/20 text-xs font-bold text-[hsl(var(--utu-gold))]">3</div>
                  <div>
                    <h5 className="font-semibold text-white text-sm">Sovereign Handover</h5>
                    <p className="text-xs text-white/60">Deploy containerized packages on private VPNs and hand over repository keys.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </GlassCard>
      </section>
    </Layout>
  );
}
